import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshControl, StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ErrorState } from '../../components/ErrorState';
import { ScreenHeader } from '../../components/ScreenHeader';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { getMyProfile } from '../../services/profileService';
import type { ProfileView } from '../../types/profile';
import { formatApiError } from '../../utils/formatApiError';

type Props = NativeStackScreenProps<RootStackParamList, 'ProfileDetails'>;

function displayValue(fieldValue: unknown) {
  return fieldValue === null || fieldValue === undefined || fieldValue === ''
    ? 'Not available'
    : String(fieldValue);
}

function Field({
  label,
  value,
  palette,
}: {
  label: string;
  value: string | null | undefined;
  palette: ColorPalette;
}) {
  return (
    <View style={styles.field} accessibilityLabel={`${label}: ${displayValue(value)}`}>
      <Text style={[styles.label, { color: palette.textMuted }]}>{label}</Text>
      <Text style={[styles.value, { color: palette.textPrimary }]}>
        {displayValue(value)}
      </Text>
    </View>
  );
}

export function ProfileDetailsScreen({ navigation }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const canViewEmployees = auth.hasPermission(PERMISSIONS.EMPLOYEE_VIEW);
  const canViewOrganization = auth.hasPermission(PERMISSIONS.SETTINGS_VIEW);
  const [profile, setProfile] = useState<ProfileView | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(
    async (isRefresh = false) => {
      isRefresh ? setRefreshing(true) : setLoading(true);
      setError('');
      try {
        const next = await getMyProfile({
          canViewEmployees,
          canViewOrganization,
        });
        setProfile(next);
      } catch (caught) {
        setError(
          formatApiError(caught).message || 'Unable to load your profile.',
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [canViewEmployees, canViewOrganization],
  );

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  const themed = useMemo(
    () => ({
      name: { ...typography.sectionTitle, color: palette.textPrimary },
    }),
    [palette],
  );

  if (loading && !profile) {
    return (
      <AppScreen>
        <ScreenHeader title="My profile" onBack={navigation.goBack} />
        <AppLoader message="Loading profile" />
      </AppScreen>
    );
  }

  if (error && !profile) {
    return (
      <AppScreen>
        <ScreenHeader title="My profile" onBack={navigation.goBack} />
        <ErrorState message={error} onRetry={() => load()} />
      </AppScreen>
    );
  }

  return (
    <AppScreen
      scroll
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={() => load(true)} />
      }
    >
      <ScreenHeader title="My profile" onBack={navigation.goBack} />
      <View style={styles.body}>
        <AppCard style={styles.card}>
          <Text style={themed.name} accessibilityRole="header">
            {displayValue(profile?.displayName)}
          </Text>
          <Field label="Employee number" value={profile?.employeeNumber} palette={palette} />
          <Field label="Designation" value={profile?.designation} palette={palette} />
          <Field label="Department" value={profile?.department} palette={palette} />
          <Field label="Organization" value={profile?.organizationName} palette={palette} />
          <Field label="Manager" value={profile?.manager} palette={palette} />
          <Field label="Email" value={profile?.email} palette={palette} />
          <Field label="Mobile" value={profile?.mobile} palette={palette} />
          <Field label="Joining date" value={profile?.joiningDate} palette={palette} />
          <Field label="Status" value={profile?.status} palette={palette} />
          <Field
            label="Roles"
            value={profile?.roles?.length ? profile.roles.join(', ') : null}
            palette={palette}
          />
        </AppCard>
      </View>
    </AppScreen>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  card: { gap: spacing.md, marginBottom: spacing.lg },
  field: { gap: spacing.xs },
  label: { ...typography.caption, fontWeight: '600' },
  value: { ...typography.body },
});
